import { useEffect, useRef } from "react";
import { getPubNubInstance } from "../config/pubnub";
import type { PubNubMessage } from "@sajilni/real-time";

type ParsedNotificationPayload = {
  type?: string;
  id?: string;
  senderId?: string;
  content?: string;
  notification?: {
    id?: string;
    sender?: { name?: string };
  } & Record<string, unknown>;
};

export type LiveNotificationPayload = { id: string } & Record<string, unknown>;

function notificationIdOf(parsed: ParsedNotificationPayload): string {
  const nestedId = parsed.notification?.id;
  if (typeof nestedId === "string" && nestedId) return nestedId;
  if (typeof parsed.id === "string" && parsed.id) return parsed.id;
  return "";
}

function parseNotificationPayload(
  message: PubNubMessage
): ParsedNotificationPayload | null {
  const raw = message.message as unknown;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as ParsedNotificationPayload;
  } catch {
    return null;
  }
}

// ponytail: one channel subscription shared by bell + full-page inbox.
// Per-instance subscribe/unsubscribe would drop the other consumer.
const liveHandlers = new Set<(notification: LiveNotificationPayload) => void>();
let liveUserId: string | undefined;
let liveBound = false;

function dispatchLiveNotification(notification: LiveNotificationPayload) {
  liveHandlers.forEach((handler) => handler(notification));
}

function bindLiveChannel(userId: string) {
  if (liveBound && liveUserId === userId) return;
  const pubNub = getPubNubInstance(userId);
  if (!pubNub) return;
  if (liveBound && liveUserId && liveUserId !== userId) {
    pubNub.unsubscribeFromChannels([liveUserId]);
  }
  liveUserId = userId;
  liveBound = true;
  pubNub.subscribeToChannels([userId], (message) => {
    const parsedMessage = parseNotificationPayload(message);
    if (!parsedMessage || parsedMessage.type !== "notification") return;
    const id = notificationIdOf(parsedMessage);
    if (!id) return;
    dispatchLiveNotification({
      ...(parsedMessage.notification ?? {}),
      id,
    });
  });
}

export function usePubNubNotifications(
  userId: string | undefined,
  onNotification?: (notification: LiveNotificationPayload) => void
) {
  const onNotificationRef = useRef(onNotification);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    if (!userId) return;
    const handler = (notification: LiveNotificationPayload) => {
      onNotificationRef.current?.(notification);
    };
    liveHandlers.add(handler);
    bindLiveChannel(userId);

    return () => {
      liveHandlers.delete(handler);
      if (liveHandlers.size === 0 && liveBound && liveUserId === userId) {
        getPubNubInstance(userId)?.unsubscribeFromChannels([userId]);
        liveBound = false;
        liveUserId = undefined;
      }
    };
  }, [userId]);
}
